import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Container,
  Tooltip,
} from '@mui/material';
import {
  Person,
  History,
  Book,
  Favorite,
  Note,
  Edit,
  Lock,
  Notifications,
  Logout,
  Refresh,
  RequestPage,
  FilterList,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ApiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../services/api';
import Header from '../components/layout/Header';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Profile = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [renewalReason, setRenewalReason] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Fetch profile summary
  const {
    data: profileSummary,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery(
    ['profileSummary', user?.id],
    () => ApiService.getProfileSummary(user.id),
    {
      enabled: !!user?.id,
    }
  );

  // Fetch reading history (in-house usage) 
  const {
    data: readingHistory,
    isLoading: readingHistoryLoading,
  } = useQuery(
    ['readingHistory'],
    () => ApiService.getReadingHistoryByStatus(null, { page: 1, limit: 10 }),
    {
      enabled: !!user?.id,
    }
  );

  // Fetch borrow history
  const {
    data: borrowHistory,
    isLoading: historyLoading,
  } = useQuery(
    ['borrowHistory', user?.id],
    () => ApiService.getBorrowHistory(user.id, { page: 1, limit: 10 }),
    {
      enabled: !!user?.id,
      onSuccess: (data) => {
        // console.log('Borrow History Data:', data);
        // console.log('Borrow History Data Length:', data?.data?.length);
        // console.log('Borrow History Type:', typeof data);
        // console.log('Borrow History Data Property:', data?.data);
      },
      onError: (error) => {
        console.error('Borrow History Error:', error);
      },
    }
  );

  // Fetch favorites
  const {
    data: favorites,
    isLoading: favoritesLoading,
  } = useQuery(
    ['favorites', user?.id],
    () => ApiService.getFavorites(user.id, { page: 1, limit: 10 }),
    {
      enabled: !!user?.id,
    }
  );

  // Fetch book requests
  const {
    data: bookRequests,
    isLoading: bookRequestsLoading,
  } = useQuery(
    ['bookRequests', user?.id],
    () => ApiService.getMyBookRequests(),
    {
      enabled: !!user?.id,
    }
  );

  // Fetch notes
  const {
    data: notes,
    isLoading: notesLoading,
  } = useQuery(
    ['notes', user?.id],
    () => ApiService.getUserNotes(user.id, { page: 1, limit: 10 }),
    {
      enabled: !!user?.id,
    }
  );

  // Check if loan has pending renewal request
  const hasPendingRenewal = (loanId) => {
    if (!bookRequests || !Array.isArray(bookRequests)) return false;
    return bookRequests.some(request => 
      request.loanId === loanId && 
      (request.status === 'RENEWAL_PENDING' || request.status === 'RENEWAL_APPROVED')
    );
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRenewalRequest = async () => {
    try {
      await ApiService.createRenewalRequest(selectedLoan.id, renewalReason);
      setRenewalDialogOpen(false);
      setRenewalReason('');
      setSelectedLoan(null);
      setSnackbar({
        open: true,
        message: 'Renewal request submitted successfully! Awaiting librarian approval.',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to submit renewal request',
        severity: 'error'
      });
    }
  };

  const openRenewalDialog = (loan) => {
    setSelectedLoan(loan);
    setRenewalDialogOpen(true);
  };

  const closeRenewalDialog = () => {
    setRenewalDialogOpen(false);
    setSelectedLoan(null);
    setRenewalReason('');
  };

  const handleEditProfileClick = () => {
    setEditFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
    });
    setEditProfileDialogOpen(true);
  };

  const handleChangePasswordClick = () => {
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setChangePasswordDialogOpen(true);
  };

  const handleEditProfileSubmit = async () => {
    try {
      await ApiService.updateProfile(user.id, editFormData);
      setEditProfileDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      // TODO: Refresh user data in auth context
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update profile',
        severity: 'error'
      });
    }
  };

  const handleChangePasswordSubmit = async () => {
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error'
      });
      return;
    }

    try {
      await ApiService.changePassword({
        currentPassword: passwordFormData.currentPassword,
        newPassword: passwordFormData.newPassword,
        confirmPassword: passwordFormData.confirmPassword,
      });
      setChangePasswordDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Password changed successfully!',
        severity: 'success'
      });
      // Clear password form
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to change password',
        severity: 'error'
      });
    }
  };

  const getUserInitials = () => {
    if (!user) return '';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6">Please sign in to view your profile</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/login')}
        >
          Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Profile Header */}
        <Paper sx={{ p: 4, mb: 2, mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: 18 }}>
              My Profile
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={user.avatarUrl}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                >
                  {getUserInitials()}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: 16 }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.degree} {user.semester && `• ${user.semester}`} {user.role?.name && `• ${user.role.name}`}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              {profileLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading profile information...</Typography>
                </Box>
              ) : profileError ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Error loading profile information
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    Please try refreshing the page
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.email || user.email}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Student ID
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.rollNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Semester
                      </Typography>
                      <Typography variant="body1">
                        {user.semester || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Program
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.program || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.phoneNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Member Since
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.joinedAt 
                          ? new Date(profileSummary.joinedAt).getFullYear()
                          : 'N/A'
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Membership Status
                        {profileSummary?.membershipStatus === 'inactive' && (
                          <Tooltip 
                            title="Your library membership is currently inactive. Please contact the librarian to activate your membership to explore more of the library."
                            arrow
                            placement="top"
                          >
                            <Warning 
                              sx={{ 
                                ml: 1, 
                                color: 'warning.main', 
                                fontSize: 16,
                                cursor: 'help',
                                verticalAlign: 'middle'
                              }} 
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.membershipStatus || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Membership Type
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.membershipType || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Expiry Date
                      </Typography>
                      <Typography variant="body1">
                        {profileSummary?.expiryDate 
                          ? new Date(profileSummary.expiryDate).getFullYear()
                          : 'N/A'
                        }
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<Person />} label="Profile" />
            <Tab icon={<History />} label="Reading History" />
            <Tab icon={<Book />} label="Borrowing" />
            <Tab icon={<RequestPage />} label="Requests" />
            <Tab icon={<Favorite />} label="Favorites" />
            <Tab icon={<Note />} label="Notes" />
          </Tabs>

          {/* Profile Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: 14, fontWeight: 'bold', color: '#000000' }}>Account Settings</Typography>
            <List>
              <ListItem button onClick={handleEditProfileClick}>
                <ListItemIcon>
                  <Edit />
                </ListItemIcon>
                <ListItemText primary="Edit Profile" />
              </ListItem>
              <Divider />
              <ListItem button onClick={handleChangePasswordClick}>
                <ListItemIcon>
                  <Lock />
                </ListItemIcon>
                <ListItemText primary="Change Password" />
              </ListItem>
              <Divider />
              <ListItem button>
                <ListItemIcon>
                  <Notifications />
                </ListItemIcon>
                <ListItemText primary="Notification Settings" />
              </ListItem>
              <Divider />
              <ListItem button onClick={handleLogout}>
                <ListItemIcon>
                  <Logout color="error" />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error' }} />
              </ListItem>
            </List>
          </TabPanel>

          {/* Reading History Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: 14, fontWeight: 'bold', color: '#000000' }}>Reading History</Typography>
            {readingHistoryLoading ? (
              <Typography>Loading reading history...</Typography>
            ) : readingHistory?.items?.length > 0 ? (
              <Grid container spacing={2}>
                {readingHistory.items.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', gap: 2, pb: 2 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 120,
                            flexShrink: 0,
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={getImageUrl(item.copy?.book?.coverImageUrl)}
                            alt={item.copy?.book?.title || 'Book cover'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.src = '/assets/default_book.jpg';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                            }}
                          >
                            <Chip
                              label={item.status?.toUpperCase() || 'UNKNOWN'}
                              size="small"
                              color={
                                item.status === 'active' ? 'success' :
                                item.status === 'completed' ? 'primary' :
                                item.status === 'force_ended' ? 'error' : 'default'
                              }
                              sx={{ fontSize: 9, height: 20 }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 1, lineHeight: 1.2 }}>
                              {item.copy?.book?.title || 'Unknown Book'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                              <strong>Started:</strong> {item.startedAt ? new Date(item.startedAt).toLocaleDateString() : 'N/A'}
                            </Typography>
                            {item.endedAt && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Ended:</strong> {new Date(item.endedAt).toLocaleDateString()}
                              </Typography>
                            )}
                            {item.duration && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Duration:</strong> {item.duration}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No reading history found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your reading history will appear here
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Borrowing Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: 14, fontWeight: 'bold', color: '#000000' }}>Current Borrowing</Typography>
            {historyLoading ? (
              <Typography>Loading current borrowing...</Typography>
            ) : (
              <>
                {borrowHistory?.data?.length > 0 ? (
                  <Grid container spacing={2}>
                    {borrowHistory.data.map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', gap: 2, pb: 2 }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 120,
                                flexShrink: 0,
                                borderRadius: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                marginBottom: 1,
                              }}
                            >
                              <img
                                src={getImageUrl(item.bookCopy?.book?.coverImageUrl || item.book?.coverImageUrl)}
                                alt={item.bookCopy?.book?.title || item.book?.title || 'Book cover'}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                                onError={(e) => {
                                  e.target.src = '/assets/default_book.jpg';
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                }}
                              >
                                <Chip
                                  label={item.status?.toUpperCase() || 'UNKNOWN'}
                                  size="small"
                                  color={
                                    item.status === 'active' ? 'success' :
                                    item.status === 'returned' ? 'primary' :
                                    item.status === 'overdue' ? 'error' : 'default'
                                  }
                                  sx={{ fontSize: 9, height: 20 }}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 1, lineHeight: 1.2 }}>
                                  {item.bookCopy?.book?.title || item.book?.title || 'Unknown Book'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                  <strong>Borrowed:</strong> {item.borrowedAt ? new Date(item.borrowedAt).toLocaleDateString() : 'N/A'}
                                </Typography>
                                {item.dueDate && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                    <strong>Due:</strong> {new Date(item.dueDate).toLocaleDateString()}
                                  </Typography>
                                )}
                                {item.returnedAt && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                    <strong>Returned:</strong> {new Date(item.returnedAt).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                              {/* Add renewal button for active loans */}
                              {(!item.returnedAt && (item.status === 'active' || item.status === 'ACTIVE' || item.status?.toUpperCase() === 'ACTIVE') && !hasPendingRenewal(item.id)) && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Refresh />}
                                    onClick={() => openRenewalDialog(item)}
                                    sx={{ 
                                      fontSize: 10, 
                                      py: 0.5, 
                                      px: 1,
                                      borderColor: '#1976d2',
                                      color: '#1976d2',
                                      '&:hover': {
                                        borderColor: '#1565c0',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                      }
                                    }}
                                  >
                                    Request Renewal
                                  </Button>
                                </Box>
                              )}

                              {/* Show renewal status if there's a pending renewal */}
                              {hasPendingRenewal(item.id) && (
                                <Box sx={{ mt: 1 }}>
                                  <Chip
                                    label="Renewal Requested"
                                    size="small"
                                    color="warning"
                                    sx={{ fontSize: 9, height: 20 }}
                                  />
                                </Box>
                              )}
                              
                              {/* Debug info - remove this later */}
                              {
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8 }}>
                                  status="{item.status}" | returnedAt={item.returnedAt ? 'yes' : 'no'}
                                </Typography>
                              }
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      No borrow history found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your borrowing history will appear here
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          {/* Requests Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 'bold', color: '#000000' }}>My Book Requests</Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="request-status-filter-label">Status</InputLabel>
                <Select
                  labelId="request-status-filter-label"
                  value={requestStatusFilter}
                  label="Status"
                  onChange={(e) => setRequestStatusFilter(e.target.value)}
                  startAdornment={<FilterList sx={{ mr: 1, fontSize: 16 }} />}
                >
                  <MenuItem value="all">All Requests</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="FULFILLED">Fulfilled</MenuItem>
                  <MenuItem value="RENEWAL_PENDING">Renewal Pending</MenuItem>
                  <MenuItem value="RENEWAL_APPROVED">Renewal Approved</MenuItem>
                  <MenuItem value="RENEWAL_REJECTED">Renewal Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {bookRequestsLoading ? (
              <Typography>Loading book requests...</Typography>
            ) : bookRequests && bookRequests.length > 0 ? (
              <Grid container spacing={2}>
                {bookRequests
                  .filter(request => requestStatusFilter === 'all' || request.status === requestStatusFilter)
                  .map((request) => (
                  <Grid item xs={12} sm={6} md={4} key={request.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', gap: 2, pb: 2 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 120,
                            flexShrink: 0,
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={getImageUrl(request.book?.coverImageUrl)}
                            alt={request.book?.title || 'Book cover'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.src = '/assets/default_book.jpg';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                            }}
                          >
                            <Chip
                              label={request.status?.toUpperCase() || 'UNKNOWN'}
                              size="small"
                              color={
                                request.status === 'APPROVED' ? 'success' :
                                request.status === 'REJECTED' ? 'error' :
                                request.status === 'PENDING' ? 'warning' :
                                request.status === 'FULFILLED' ? 'info' :
                                request.status === 'RENEWAL_PENDING' ? 'warning' :
                                request.status === 'RENEWAL_APPROVED' ? 'success' :
                                request.status === 'RENEWAL_REJECTED' ? 'error' :
                                'default'
                              }
                              sx={{ fontSize: 9, height: 20 }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 1, lineHeight: 1.2 }}>
                              {request.book?.title || 'Unknown Book'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, mb: 1 }}>
                              {request.book?.author || 'Unknown Author'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mb: 1 }}>
                              {request.requestType === 'RENEWAL' ? 'Renewal Request' : 'Borrow Request'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                              <strong>Requested:</strong> {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}
                            </Typography>
                            {request.approvedAt && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Approved:</strong> {new Date(request.approvedAt).toLocaleDateString()}
                              </Typography>
                            )}
                            {request.rejectedAt && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Rejected:</strong> {new Date(request.rejectedAt).toLocaleDateString()}
                              </Typography>
                            )}
                            {request.reason && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Reason:</strong> {request.reason}
                              </Typography>
                            )}
                            {request.rejectionReason && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Rejection Reason:</strong> {request.rejectionReason}
                              </Typography>
                            )}
                            {request.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                <strong>Notes:</strong> {request.notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  {requestStatusFilter === 'all' 
                    ? 'No book requests found' 
                    : `No ${requestStatusFilter.toLowerCase().replace('_', ' ')} requests found`
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {requestStatusFilter === 'all' 
                    ? 'Your book requests will appear here'
                    : 'Try changing the status filter to see more requests'
                  }
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Favorites Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: 14, fontWeight: 'bold', color: '#000000' }}>Favorite Books</Typography>
            {favoritesLoading ? (
              <Typography>Loading favorites...</Typography>
            ) : favorites?.data?.length > 0 ? (
              <Grid container spacing={2}>
                {favorites.data.map((book) => (
                  <Grid item xs={12} sm={6} md={4} key={book.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', gap: 2, pb: 2 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 120,
                            flexShrink: 0,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={getImageUrl(book.coverImageUrl)}
                            alt={book.title || 'Book cover'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.src = '/assets/default_book.jpg';
                            }}
                          />
                        </Box>
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 1, lineHeight: 1.2 }}>
                              {book.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, mb: 1 }}>
                              {book.author}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                              <strong>ISBN:</strong> {book.isbn || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                              <strong>Year:</strong> {book.publicationYear || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No favorite books yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your favorite books will appear here
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Notes Tab */}
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" sx={{ mb: 3, fontSize: 14, fontWeight: 'bold', color: '#000000' }}>My Notes</Typography>
            {notesLoading ? (
              <Typography>Loading notes...</Typography>
            ) : notes?.data?.length > 0 ? (
              <List>
                {notes.data.map((note) => (
                  <ListItem key={note.id}>
                    <ListItemText
                      primary={note.content?.substring(0, 100) || 'No content'}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Book: {note.book?.title || 'Unknown Book'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Page: {note.pageNumber || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Created: {note.createdAt ? new Date(note.createdAt).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No notes yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your book notes will appear here
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Paper>
      </Container>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileDialogOpen} onClose={() => setEditProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              fullWidth
              value={editFormData.firstName}
              onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={editFormData.lastName}
              onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={editFormData.phoneNumber}
              onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProfileDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditProfileSubmit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              value={passwordFormData.currentPassword}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={passwordFormData.newPassword}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              value={passwordFormData.confirmPassword}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChangePasswordSubmit} variant="contained" color="primary">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renewal Request Dialog */}
      <Dialog open={renewalDialogOpen} onClose={closeRenewalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Request Loan Renewal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Book: {selectedLoan?.bookCopy?.book?.title || selectedLoan?.book?.title || 'Unknown Book'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current Due Date: {selectedLoan?.dueDate ? new Date(selectedLoan.dueDate).toLocaleDateString() : 'N/A'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Renewal (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={renewalReason}
            onChange={(e) => setRenewalReason(e.target.value)}
            placeholder="Please provide a reason for your renewal request..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenewalDialog}>Cancel</Button>
          <Button onClick={handleRenewalRequest} variant="contained" color="primary">
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
